/**
 * Google Analytics 4 — SPA route-change pageview tracking.
 *
 * The gtag.js loader + initial `gtag('config', ..., { send_page_view: false })`
 * lives in /app/frontend/public/index.html so the tag loads once globally.
 *
 * This hook fires exactly one `page_view` event per route change AFTER the
 * page's `useSeo` effect has set `document.title`, so GA records the correct
 * page_title for every URL. Admin routes are excluded.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_MEASUREMENT_ID = "G-82RMN6EY8H";

// Google Ads purchase conversion (Enhanced Conversions enabled account-side).
const GOOGLE_ADS_PURCHASE_SEND_TO = "AW-18309865821/JFsMCKqD4tEcEN3C6ZpE";

/**
 * Fire the Google Ads purchase conversion exactly once for a verified,
 * paid order. Value/currency/transaction_id come from the real order.
 * Deduped by order id in localStorage so a confirmation-page refresh or
 * revisit never re-reports. No-op if gtag is unavailable or the order is
 * missing a positive total (never sends value:1.0 or an empty id).
 */
export function trackGoogleAdsPurchase(order) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (!order || !order.id) return;
  const value = Number(order.subtotal);
  if (!Number.isFinite(value) || value <= 0) return;
  const key = `lag_gads_conv_${order.id}`;
  try {
    if (window.localStorage.getItem(key)) return; // already reported
  } catch { /* ignore storage errors */ }
  window.gtag("event", "conversion", {
    send_to: GOOGLE_ADS_PURCHASE_SEND_TO,
    value,
    currency: "USD",
    transaction_id: order.id,
  });
  try { window.localStorage.setItem(key, "1"); } catch { /* ignore */ }
}

function isTrackable(pathname) {
  if (!pathname) return false;
  if (pathname.startsWith("/admin")) return false;
  return true;
}

/**
 * Fire a custom GA4 event (e.g. vehicle_search, vehicle_select).
 * No-op when gtag is unavailable so it never breaks the UI.
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", name, { send_to: GA_MEASUREMENT_ID, ...params });
}

export function useAnalyticsPageviews() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    if (!isTrackable(location.pathname)) return;

    // Defer one tick so the page component's useSeo effect has updated
    // document.title before we read it for the event payload.
    const handle = window.setTimeout(() => {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
        send_to: GA_MEASUREMENT_ID,
      });
    }, 0);

    return () => window.clearTimeout(handle);
  }, [location.pathname, location.search]);
}
